import { createSlice } from '@reduxjs/toolkit';
import { UPLOAD_STATUS } from '../../constants/uploadStatus';

const uploadProgressSlice = createSlice({
    name: 'uploadProgress',
    initialState: {
        activeUploads: [], // Array of active upload sessions
        completedUploadSummary: null, // Summary data for modal popup after upload finishes
    },
    reducers: {
        // Start a new upload session
        startUploadSession: (state, action) => {
            const { uploadId, datasetName, datasetId, businessUnit, project, contract, dataProviderName, files, skippedFilesCount } = action.payload;
            
            state.activeUploads.push({
                uploadId,
                datasetName,
                datasetId,
                businessUnit,
                project,
                contract,
                dataProviderName,
                skippedFilesCount: skippedFilesCount || 0,
                files: files.map(file => ({
                    id: file.id,
                    name: file.name,
                    size: file.size,
                    relativePath: file.relativePath,
                    progress: 0,
                    uploadedBytes: 0,
                    status: UPLOAD_STATUS.PENDING,
                    error: null
                })),
                overallProgress: 0,
                status: UPLOAD_STATUS.IN_PROGRESS,
                startedAt: new Date().toISOString(),
                completedAt: null,
                error: null
            });
        },

        // Update progress for a specific file
        updateFileProgress: (state, action) => {
            const { uploadId, fileId, progress, uploadedBytes } = action.payload;
            const upload = state.activeUploads.find(u => u.uploadId === uploadId);
            if (upload && upload.status === UPLOAD_STATUS.PAUSED) {
                return;
            }
            if (upload) {
                const file = upload.files.find(f => f.id === fileId);
                if (file) {
                    const safeProgress = Math.min(progress, 100);
                    file.progress = safeProgress;
                
                    // Old version of safeguard caused NaN when uploadedBytes was undefined (e.g. during checksum phase),
                    // which broke overall progress calculation to 0%. This check skips the update
                    // when uploadedBytes is not provided, keeping the previously saved value intact.
                    // Only update bytes if provided; keeps saved value when not sent (prevents NaN)
                    if (uploadedBytes !== undefined && uploadedBytes !== null) {
                        file.uploadedBytes = Math.min(uploadedBytes, file.size);
                    }
                    
                    const totalBytes = upload.files.reduce((sum, f) => sum + f.size, 0);
                    const uploadedTotal = upload.files.reduce((sum, f) => sum + Math.min(f.uploadedBytes || 0, f.size), 0);
                    const calculatedProgress = Math.round((uploadedTotal / totalBytes) * 100);
                    upload.overallProgress = Math.max(0, Math.min(100, calculatedProgress));

                    if (uploadedTotal > totalBytes) {
                        console.error(`❌ IMPOSSIBLE STATE: uploadedTotal (${uploadedTotal}) > totalBytes (${totalBytes})`, {
                            files: upload.files
                        });
                    }
                }
            }
        },

        // Update file status
        updateFileStatus: (state, action) => {
            const { uploadId, fileId, status, error } = action.payload;
            const upload = state.activeUploads.find(u => u.uploadId === uploadId);
            // SAFEGUARD: Ignore status updates if upload is paused (except to set PAUSED, COMPLETED, or FAILED)
            // COMPLETED/FAILED must be allowed because abort-batch-complete needs to finalize
            // files that finished uploading all chunks before the abort signal was received
            if (upload && upload.status === UPLOAD_STATUS.PAUSED && 
                status !== UPLOAD_STATUS.PAUSED && 
                status !== UPLOAD_STATUS.COMPLETED && 
                status !== UPLOAD_STATUS.FAILED) {
                return;
            }
            if (upload) {
                const file = upload.files.find(f => f.id === fileId);
                if (file) {
                    file.status = status;
                    if (error) file.error = error;
                    // Update overall upload status
                    const allCompleted = upload.files.every(f => f.status === UPLOAD_STATUS.COMPLETED);
                    const anyFailed = upload.files.some(f => 
                        f.status === UPLOAD_STATUS.FAILED || f.status === UPLOAD_STATUS.PARTIALLY_FAILED
                    );
                    const allFailed = upload.files.every(f => 
                        f.status === UPLOAD_STATUS.FAILED || f.status === UPLOAD_STATUS.PARTIALLY_FAILED
                    );
                    const anyActive = upload.files.some(f => 
                        f.status === UPLOAD_STATUS.UPLOADING || f.status === UPLOAD_STATUS.PENDING
                    );
                    if (allCompleted) {
                        upload.status = UPLOAD_STATUS.COMPLETED;
                        upload.completedAt = new Date().toISOString();
                    } else if (allFailed) {
                        upload.status = UPLOAD_STATUS.PARTIALLY_FAILED;
                        upload.completedAt = new Date().toISOString();
                    } else if (anyActive) {
                        // If any files are still uploading or pending, keep as in-progress
                        upload.status = UPLOAD_STATUS.IN_PROGRESS;
                    } else if (anyFailed) {
                        // Only mark as partially-failed if nothing is actively uploading
                        upload.status = UPLOAD_STATUS.PARTIALLY_FAILED;
                    }
                }
            }
        },

        // Cancel an upload session
        cancelUploadSession: (state, action) => {
            const { uploadId } = action.payload;
            const upload = state.activeUploads.find(u => u.uploadId === uploadId);
            
            if (upload) {      
                upload.status = UPLOAD_STATUS.PAUSED;
                upload.pausedAt = new Date().toISOString();
                upload.files = upload.files.map(file => {
                    // Only pause files that are actively uploading or pending
                    // Don't overwrite COMPLETED or FAILED status
                    if (file.status === UPLOAD_STATUS.UPLOADING || 
                        file.status === UPLOAD_STATUS.PENDING ||
                        file.status === UPLOAD_STATUS.COMPUTING_CHECKSUMS) {
                        return { ...file, status: UPLOAD_STATUS.PAUSED };
                    }
                    return file;
                });
            }
        },

        // Remove an upload session (after completion or cancellation)
        removeUploadSession: (state, action) => {
            const { uploadId } = action.payload;
            state.activeUploads = state.activeUploads.filter(u => u.uploadId !== uploadId);
        },

        // Retry failed files in an upload session
        retryFailedFiles: (state, action) => {
            const { uploadId } = action.payload;
            const upload = state.activeUploads.find(u => u.uploadId === uploadId);
            
            if (upload) {        
                upload.status = UPLOAD_STATUS.IN_PROGRESS;
                upload.files = upload.files.map(file => {
                    if (file.status === UPLOAD_STATUS.FAILED || 
                        file.status === UPLOAD_STATUS.CANCELLED || 
                        file.status === UPLOAD_STATUS.PAUSED || 
                        file.status === UPLOAD_STATUS.PARTIALLY_FAILED) {
                        const preserveProgress = file.status === UPLOAD_STATUS.PAUSED || file.status === UPLOAD_STATUS.PARTIALLY_FAILED;
                        return {
                            ...file,
                            status: UPLOAD_STATUS.PENDING,
                            progress: preserveProgress ? file.progress : 0,
                            uploadedBytes: preserveProgress ? file.uploadedBytes : 0,
                            error: null
                        };
                    }
                    return file;
                });

                // Recalculate overall progress to preserve it after resume
                const totalBytes = upload.files.reduce((sum, f) => sum + f.size, 0);
                const uploadedTotal = upload.files.reduce((sum, f) => sum + Math.min(f.uploadedBytes || 0, f.size), 0);
                upload.overallProgress = totalBytes > 0 
                    ? Math.max(0, Math.min(100, Math.round((uploadedTotal / totalBytes) * 100))) 
                    : 0;
            }
        },

        // Load active uploads from localStorage (on app start)
        loadActiveUploads: (state, action) => {
            state.activeUploads = action.payload || [];
        },

        // Set error message for an upload session
        setUploadError: (state, action) => {
            const { uploadId, error } = action.payload;
            const upload = state.activeUploads.find(u => u.uploadId === uploadId);
            
            if (upload) {
                upload.error = error;
                upload.status = UPLOAD_STATUS.FAILED;
            }
        },

        // Set completed upload summary for modal display
        setCompletedUploadSummary: (state, action) => {
            state.completedUploadSummary = action.payload;
        },

        // Clear completed upload summary (when user closes modal)
        clearCompletedUploadSummary: (state) => {
            state.completedUploadSummary = null;
        },
    }
});

export const {
    startUploadSession,
    updateFileProgress,
    updateFileStatus,
    cancelUploadSession,
    removeUploadSession,
    retryFailedFiles,
    loadActiveUploads,
    setUploadError,
    setCompletedUploadSummary,
    clearCompletedUploadSummary
} = uploadProgressSlice.actions;

export default uploadProgressSlice.reducer;
